import {
  AttributionControl,
  LngLatBounds,
  Map as MapLibreMap,
  NavigationControl,
  type ExpressionSpecification,
  type VectorTileSource,
} from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { getLocale } from '../../../i18n';
import { EmptyState } from '../../../chrome/state';
import { pmtilesSourceUrl, registerMapWorker, registerPmtilesProtocol } from '../../../lib/map';
import { Card } from '../../../ui/card';
import { tt } from './messages';
import { compact } from './NeighbourhoodsCard';

const SOURCE = 'territory';
/** The layer the tiles pipeline writes surveyed boundaries into. */
const BOUNDARIES = 'localities';

/** MapLibre parses hex, rgb and hsl but not the oklch the tokens are written
 *  in, so a token is read back through a canvas, which resolves any CSS colour
 *  to rgb. */
function tokenColour(name: string): string {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const context = document.createElement('canvas').getContext('2d');
  if (context === null) return raw;
  context.fillStyle = raw;
  context.fillRect(0, 0, 1, 1);
  const [r, g, b] = context.getImageData(0, 0, 1, 1).data;
  return `rgb(${r}, ${g}, ${b})`;
}

/** The five steps of the scale, lightest first: the token MapLibre paints
 *  and the utility the legend draws it with. */
const SCALE = [1, 2, 3, 4, 5].map((n) => ({
  token: `--color-scale-${n}`,
  swatch: `bg-scale-${n}`,
}));
/** A locality with no figure is left unfilled, never painted as zero. */
const UNSHADED = 'rgba(0, 0, 0, 0)';

/** Each boundary's figure on the rail's scale, keyed by the tile feature's
 *  `id`, which is the locality id. `max` is the largest figure the route
 *  returned, `null` when it returned none. */
export interface MapShading {
  values: ReadonlyArray<readonly [localityId: string, value: number | null]>;
  max: number | null;
}

function step(value: number, max: number): number {
  return max <= 0 ? 0 : Math.min(SCALE.length - 1, Math.floor((value / max) * SCALE.length));
}

/** Without a reading the boundaries keep their plain fill; with one, each
 *  locality takes its step on the scale and the rest are unfilled. */
function fillColour(shading: MapShading | null): string | ExpressionSpecification {
  if (shading === null) return tokenColour('--color-scale-2');
  const { max } = shading;
  const colours = SCALE.map(({ token }) => tokenColour(token));
  const pairs = shading.values.flatMap(([localityId, value]) =>
    value === null || max === null ? [] : [localityId, colours[step(value, max)] ?? UNSHADED],
  );
  if (pairs.length === 0) return UNSHADED;
  // The spec's `match` tuple demands a label/output pair the spread cannot
  // prove; the guard above is that proof.
  return ['match', ['get', 'id'], ...pairs, UNSHADED] as unknown as ExpressionSpecification;
}

/** Where a locality lies, read from the tiles already drawn; `null` until one
 *  holding it has been. */
function boundsOf(map: MapLibreMap, localityId: string): LngLatBounds | null {
  const bounds = new LngLatBounds();
  const walk = (coordinates: unknown): void => {
    if (!Array.isArray(coordinates)) return;
    if (typeof coordinates[0] === 'number') bounds.extend(coordinates as [number, number]);
    else coordinates.forEach(walk);
  };
  const features = map.querySourceFeatures(SOURCE, {
    sourceLayer: BOUNDARIES,
    filter: ['==', ['get', 'id'], localityId],
  });
  for (const { geometry } of features) {
    if ('coordinates' in geometry) walk(geometry.coordinates);
  }
  return bounds.isEmpty() ? null : bounds;
}

function Legend(props: { max: number }): ReactNode {
  const label = tt('map.legend', { max: compact(props.max, getLocale()) });
  return (
    <div
      role="img"
      aria-label={label}
      className="bg-card rounded-pill absolute bottom-3 left-3 flex items-center gap-1 px-3 py-1.5 shadow-xs"
    >
      {SCALE.map(({ swatch }) => (
        <span key={swatch} aria-hidden className={`h-2 w-4 rounded-pill ${swatch}`} />
      ))}
      <span aria-hidden className="text-caption text-muted-foreground ml-1" data-numeric>
        {compact(props.max, getLocale())}
      </span>
    </div>
  );
}

/**
 * The territory's boundaries, read straight from the PMTiles archive over its
 * own delivery path: the archive carries public boundaries, no one's personal
 * data, so no tool is involved (platform design §17.10). The two ways the map
 * is unavailable — no archive configured, and an archive that cannot be read —
 * are different facts about the campaign, so the card names which one it is;
 * neither promises the map will arrive.
 */
export function TerritoryMap(props: {
  tilesUrl: string;
  compact?: boolean;
  shading?: MapShading | null;
  /** The locality the map opens on, once its tiles are drawn. */
  focus?: string;
}): ReactNode {
  const { tilesUrl, compact: thumbnail = false, shading = null, focus } = props;
  const container = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const shadingRef = useRef(shading);
  const [failedFor, setFailedFor] = useState<string | null>(null);
  const available = tilesUrl !== '' && failedFor !== tilesUrl;
  // A thumbnail mount sizes down to fit its own content instead of cropping
  // a full-size box: `min-h` lets the empty state's icon and copy still
  // claim the space they need rather than being clipped off-screen.
  const height = thumbnail ? 'min-h-40' : 'min-h-96';

  useEffect(() => {
    const element = container.current;
    if (!available || element === null) return;

    registerMapWorker();
    registerPmtilesProtocol();
    const map = new MapLibreMap({
      container: element,
      style: { version: 8, sources: {}, layers: [] },
      attributionControl: false,
      locale: {
        'Map.Title': tt('map.label'),
        'NavigationControl.ZoomIn': tt('map.zoomIn'),
        'NavigationControl.ZoomOut': tt('map.zoomOut'),
        'AttributionControl.ToggleAttribution': tt('map.attributionToggle'),
      },
    });
    map.addControl(new NavigationControl({ showCompass: false }), 'top-right');
    map.addControl(
      new AttributionControl({ compact: true, customAttribution: tt('map.attribution') }),
    );
    // MapLibre opens a compact attribution expanded on first render; the
    // licences only need to be reachable, so it starts behind its button.
    const collapseAttribution = (): void => {
      const details = element.querySelector('.maplibregl-ctrl-attrib');
      details?.classList.remove('maplibregl-compact-show');
      details?.removeAttribute('open');
    };
    collapseAttribution();

    // The archive's header arrives as the source's metadata. An error before
    // it is an unreadable archive; one after it is a single missing tile.
    let headerRead = false;
    map.on('sourcedata', (event) => {
      if (event.sourceId !== SOURCE || event.sourceDataType !== 'metadata') return;
      headerRead = true;
      const source = map.getSource(SOURCE) as VectorTileSource | undefined;
      // Below the archive's lowest zoom there are no tiles to draw, and a
      // whole-country archive fitted to a card lands below it.
      if (source !== undefined) map.setMinZoom(source.minzoom);
      const bounds = source?.bounds;
      if (bounds !== undefined) {
        map.fitBounds([bounds[0], bounds[1], bounds[2], bounds[3]], {
          padding: 16,
          animate: false,
        });
      }
    });
    let focused = focus === undefined;
    map.on('idle', () => {
      if (focused || focus === undefined) return;
      const bounds = boundsOf(map, focus);
      if (bounds === null) return;
      focused = true;
      map.fitBounds(bounds, { padding: 16, animate: false });
    });
    map.on('error', () => {
      if (!headerRead) setFailedFor(tilesUrl);
    });
    map.on('load', () => {
      collapseAttribution();
      map.addSource(SOURCE, { type: 'vector', url: pmtilesSourceUrl(tilesUrl) });
      map.addLayer({
        id: 'boundaries-fill',
        type: 'fill',
        source: SOURCE,
        'source-layer': BOUNDARIES,
        paint: { 'fill-color': fillColour(shadingRef.current) },
      });
      map.addLayer({
        id: 'boundaries-line',
        type: 'line',
        source: SOURCE,
        'source-layer': BOUNDARIES,
        paint: { 'line-color': tokenColour('--color-scale-5'), 'line-width': 1 },
      });
      mapRef.current = map;
    });

    return () => {
      mapRef.current = null;
      map.remove();
    };
  }, [tilesUrl, available, focus]);

  useEffect(() => {
    shadingRef.current = shading;
    mapRef.current?.setPaintProperty('boundaries-fill', 'fill-color', fillColour(shading));
  }, [shading]);

  return (
    <Card className={`${height} overflow-hidden py-0`}>
      {available ? (
        <div className="relative flex flex-1 flex-col">
          <div
            ref={container}
            role="region"
            aria-label={tt('map.label')}
            className={`${height} flex-1 [&_.maplibregl-ctrl-attrib]:rounded-md! [&_.maplibregl-ctrl-attrib]:bg-card! [&_.maplibregl-ctrl-attrib]:text-caption! [&_.maplibregl-ctrl-attrib]:text-muted-foreground! [&_.maplibregl-ctrl-attrib]:shadow-1! [&_.maplibregl-ctrl-attrib-button]:bg-card! [&_.maplibregl-ctrl-attrib-button]:focus-visible:ring-2! [&_.maplibregl-ctrl-attrib-button]:focus-visible:ring-ring! [&_.maplibregl-ctrl-attrib-button]:focus-visible:outline-none!`}
          />
          {shading !== null && shading.max !== null && <Legend max={shading.max} />}
        </div>
      ) : (
        <div className={`flex ${height} flex-1 items-center justify-center`}>
          <EmptyState
            tone="blocked"
            title={tt('map.unavailable.title')}
            description={tt(
              tilesUrl === '' ? 'map.unavailable.absent' : 'map.unavailable.unreadable',
            )}
          />
        </div>
      )}
    </Card>
  );
}
